<template>
  <div>
    <Breadcrumbs main="Users" title="Edit Profile" />

    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xl-8 col-lg-10">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5>Edit My Profile</h5>
                <span>Update your own account profile details.</span>
              </div>
              <button class="btn btn-outline-primary" type="button" @click="goToDashboard">
                Back to Dashboard
              </button>
            </div>

            <div class="card-body">
              <div v-if="errorMessage" class="alert alert-danger" role="alert">
                {{ errorMessage }}
              </div>
              <div v-if="successMessage" class="alert alert-success" role="alert">
                {{ successMessage }}
              </div>

              <div v-if="loading" class="text-center py-4">
                Loading your profile...
              </div>

              <form v-else @submit.prevent="updateProfile">
                <div class="row g-4">
                  <div class="col-md-4 text-center">
                    <div class="avatar-preview-wrap mb-3">
                      <img
                        :src="avatarImageUrl"
                        class="rounded-circle avatar-preview"
                        alt="Profile avatar"
                      />
                    </div>
                    <label class="form-label d-block">Profile Avatar</label>
                    <input
                      ref="avatarInput"
                      type="file"
                      class="form-control"
                      accept="image/*"
                      @change="handleAvatarChange"
                    />
                    <small class="text-muted d-block mt-2">JPG, PNG, GIF, or WEBP. Max 5 MB.</small>
                  </div>

                  <div class="col-md-8">
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label">Username</label>
                        <input v-model="profile.username" type="text" class="form-control" disabled />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Email</label>
                        <input
                          v-model="form.email"
                          type="email"
                          class="form-control"
                          placeholder="Enter email"
                          required
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">First Name</label>
                        <input
                          v-model="form.firstName"
                          type="text"
                          class="form-control"
                          placeholder="Enter first name"
                          required
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Last Name</label>
                        <input
                          v-model="form.lastName"
                          type="text"
                          class="form-control"
                          placeholder="Enter last name"
                          required
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Phone Number</label>
                        <input
                          v-model="form.phoneNumber"
                          type="text"
                          class="form-control"
                          placeholder="Enter phone number"
                          required
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Address</label>
                        <input
                          v-model="form.address"
                          type="text"
                          class="form-control"
                          placeholder="Enter address"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="my-4" />

                <div class="row g-3">
                  <div class="col-12">
                    <h6 class="mb-1">Change Password</h6>
                    <small class="text-muted">Leave these blank if you do not want to change your password.</small>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Old Password</label>
                    <input v-model="passwordForm.oldPassword" type="password" class="form-control" placeholder="Current password" autocomplete="current-password" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">New Password</label>
                    <input v-model="passwordForm.newPassword" type="password" class="form-control" placeholder="New password" autocomplete="new-password" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Confirm New Password</label>
                    <input v-model="passwordForm.confirmNewPassword" type="password" class="form-control" placeholder="Confirm new password" autocomplete="new-password" />
                  </div>
                </div>

                <div class="d-flex justify-content-end mt-4">
                  <button class="btn btn-primary" type="submit" :disabled="saving || !canUpdateProfile">
                    {{ saving ? 'Saving...' : 'Update Profile' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { saveStoredUserProfile } from '../../utils/auth';
import { API_BASE } from '../../config/api';
const DEFAULT_AVATAR = require('../../assets/images/dashboard/user.png');

export default {
  name: 'EditProfile',

  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      successMessage: '',
      profile: {
        id: '',
        username: '',
        avatarUrl: '',
      },
      form: {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        address: '',
        email: '',
      },
      avatarFile: null,
      avatarPreviewUrl: '',
      avatarPreviewObjectUrl: '',
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      },
    };
  },

  computed: {
    canUpdateProfile() {
      return true;
    },
    avatarImageUrl() {
      if (this.avatarPreviewUrl) {
        return this.avatarPreviewUrl;
      }

      if (this.profile.avatarUrl) {
        return this.profile.avatarUrl.startsWith('http')
          ? this.profile.avatarUrl
          : `${API_BASE}${this.profile.avatarUrl}`;
      }

      return DEFAULT_AVATAR;
    },
    wantsPasswordChange() {
      return Boolean(
        this.passwordForm.oldPassword ||
        this.passwordForm.newPassword ||
        this.passwordForm.confirmNewPassword
      );
    },
  },

  mounted() {
    this.initializeProfile();
  },

  methods: {
    authHeaders(includeJson = true) {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (includeJson) headers['Content-Type'] = 'application/json';
      return headers;
    },

    parseErrorMessage(payload, fallback) {
      if (!payload) return fallback;
      return payload.message || payload.Message || payload.title || fallback;
    },
    publishProfileUpdate(profile) {
      saveStoredUserProfile(profile);
      window.dispatchEvent(new CustomEvent('user-profile-updated'));
    },

    resetAvatarPreview() {
      if (this.avatarPreviewObjectUrl) {
        URL.revokeObjectURL(this.avatarPreviewObjectUrl);
        this.avatarPreviewObjectUrl = '';
      }
      this.avatarPreviewUrl = '';
    },

    handleAvatarChange(event) {
      const file = event.target.files && event.target.files[0];
      this.avatarFile = file || null;
      this.resetAvatarPreview();

      if (this.avatarFile) {
        this.avatarPreviewObjectUrl = URL.createObjectURL(this.avatarFile);
        this.avatarPreviewUrl = this.avatarPreviewObjectUrl;
      }
    },

    goToDashboard() {
      this.$router.push({ name: 'default' });
    },

    async initializeProfile() {
      this.errorMessage = '';
      this.successMessage = '';

      this.loading = true;

      try {
        await this.fetchUserProfile();
      } catch (error) {
        this.errorMessage = error.message || 'Failed to load your profile.';
      } finally {
        this.loading = false;
      }
    },

    async fetchUserProfile() {
      const response = await fetch(`${API_BASE}/api/User/me/profile`, {
        method: 'GET',
        headers: this.authHeaders(false),
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        throw new Error(this.parseErrorMessage(payload, `Failed to load profile (HTTP ${response.status})`));
      }

      const data = await response.json();
      this.profile.id = data.id;
      this.profile.username = data.username || this.profile.username;
      this.profile.avatarUrl = data.avatarUrl || '';
      this.publishProfileUpdate({
        username: this.profile.username,
        avatarUrl: this.profile.avatarUrl,
      });
      this.form = {
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phoneNumber: data.phoneNumber || '',
        address: data.address || '',
        email: data.email || '',
      };
    },

    async updateProfileDetails() {
      const avatarBase64 = this.avatarFile ? await this.readFileAsDataUrl(this.avatarFile) : null;
      const avatarFileName = this.avatarFile ? this.avatarFile.name : null;

      const response = await fetch(`${API_BASE}/api/User/me/profile`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          phoneNumber: this.form.phoneNumber,
          address: this.form.address,
          email: this.form.email,
          avatarBase64,
          avatarFileName,
        }),
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        throw new Error(this.parseErrorMessage(payload, `Failed to update profile (HTTP ${response.status})`));
      }

      const data = await response.json();
      this.profile.id = data.id;
      this.profile.username = data.username || this.profile.username;
      this.profile.avatarUrl = data.avatarUrl || this.profile.avatarUrl;
      this.publishProfileUpdate({
        username: this.profile.username,
        avatarUrl: this.profile.avatarUrl,
      });
      if (avatarBase64) {
        this.resetAvatarPreview();
        this.avatarFile = null;
        if (this.$refs.avatarInput) {
          this.$refs.avatarInput.value = '';
        }
      }
      this.form = {
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        phoneNumber: data.phoneNumber || '',
        address: data.address || '',
        email: data.email || '',
      };
    },

    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read avatar image.'));
        reader.readAsDataURL(file);
      });
    },

    async uploadAvatarIfNeeded() {
      if (!this.avatarFile) {
        return false;
      }

      const formData = new FormData();
      formData.append('avatar', this.avatarFile);

      const response = await fetch(`${API_BASE}/api/User/me/avatar`, {
        method: 'POST',
        headers: this.authHeaders(false),
        body: formData,
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        throw new Error(this.parseErrorMessage(payload, `Failed to upload avatar (HTTP ${response.status})`));
      }

      const data = await response.json();
      this.profile.avatarUrl = data.avatarUrl || this.profile.avatarUrl;
      this.resetAvatarPreview();
      this.avatarFile = null;
      if (this.$refs.avatarInput) {
        this.$refs.avatarInput.value = '';
      }

      return true;
    },

    async changePasswordIfNeeded() {
      if (!this.wantsPasswordChange) {
        return false;
      }

      if (
        !this.passwordForm.oldPassword ||
        !this.passwordForm.newPassword ||
        !this.passwordForm.confirmNewPassword
      ) {
        throw new Error('Fill all password fields or leave them all blank.');
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmNewPassword) {
        throw new Error('New password and confirm password must match.');
      }

      const response = await fetch(`${API_BASE}/api/User/me/password`, {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword,
          confirmNewPassword: this.passwordForm.confirmNewPassword,
        }),
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }

        throw new Error(this.parseErrorMessage(payload, `Failed to change password (HTTP ${response.status})`));
      }

      this.passwordForm.oldPassword = '';
      this.passwordForm.newPassword = '';
      this.passwordForm.confirmNewPassword = '';

      return true;
    },

    async updateProfile() {
      if (!this.canUpdateProfile) {
        return;
      }

      this.saving = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const successParts = [];
        const hadAvatarChange = Boolean(this.avatarFile);
        const hadPasswordChange = this.wantsPasswordChange;

        await this.updateProfileDetails();
        successParts.push('Profile details updated');

        if (hadAvatarChange) {
          successParts.push('Avatar updated');
        }

        const passwordUpdated = await this.changePasswordIfNeeded();
        if (hadPasswordChange && passwordUpdated) {
          successParts.push('Password updated');
        }

        this.successMessage = `${successParts.join('. ')}.`;
      } catch (error) {
        this.errorMessage = error.message || 'Failed to update your profile.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.avatar-preview-wrap {
  display: flex;
  justify-content: center;
}

.avatar-preview {
  width: 160px;
  height: 160px;
  object-fit: cover;
  border: 4px solid #e9ecef;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}
</style>
